import type { ComponentProjectMetricsResponse, DashboardMetric } from '../../../../types/dashboard';

const SERIES_META_KEYS = new Set(['quarter', 'qoq', 'yoy', 'goal', 'goalStatus', 'value']);

/** Preferred chart order; engagements first so it is not buried in the combined KPI chart. */
export const GACE_FIELD_ORDER = [
  'engagements',
  'community_members_engaged',
  'fundable_autonomous_startups',
  'improved_autonomous_systems',
  'improved_competitiveness',
  'increase_exposure_to_tech',
  'increased_ai_awareness',
  'number_of_aspire_engagements',
  'number_educational_events',
  'partner_on_grants',
  'enhanced_digital_literacy',
] as const;

/** Fields plotted together on one multi-series chart. */
export const GACE_AWARENESS_LITERACY_FIELDS = [
  'increased_ai_awareness',
  'increase_exposure_to_tech',
  'enhanced_digital_literacy',
] as const;

const GACE_AWARENESS_LITERACY_FIELD_SET = new Set<string>(GACE_AWARENESS_LITERACY_FIELDS);

type GaceChartGroup = {
  metricSuffix: string;
  fields: readonly string[];
};

/** Chart grouping plan: listed groups first (in GACE_FIELD_ORDER of their first field), then remaining singles. */
const GACE_CHART_GROUPS: GaceChartGroup[] = [
  {
    metricSuffix: 'awarenessAndLiteracy',
    fields: GACE_AWARENESS_LITERACY_FIELDS,
  },
];

export type GaceChartMetric = {
  key: string;
  metric: DashboardMetric;
  sectionTitle: string;
};

function isNumericFieldValue(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function collectFieldKeys(metric: DashboardMetric): string[] {
  const keys = new Set<string>();
  for (const point of metric.series ?? []) {
    for (const [key, value] of Object.entries(point)) {
      if (!SERIES_META_KEYS.has(key) && isNumericFieldValue(value)) {
        keys.add(key);
      }
    }
  }
  if (metric.allTime) {
    for (const [key, value] of Object.entries(metric.allTime)) {
      if (!SERIES_META_KEYS.has(key) && isNumericFieldValue(value)) {
        keys.add(key);
      }
    }
  }
  return Array.from(keys);
}

function sortFieldKeys(keys: string[]): string[] {
  const order = new Map<string, number>(GACE_FIELD_ORDER.map((key, index) => [key, index]));
  return [...keys].sort((a, b) => {
    const aOrder = order.get(a) ?? Number.MAX_SAFE_INTEGER;
    const bOrder = order.get(b) ?? Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return a.localeCompare(b);
  });
}

function pickFieldsGoal(
  goal: unknown,
  fieldKeys: readonly string[]
): Record<string, unknown> | undefined {
  if (!goal || typeof goal !== 'object') return undefined;
  const next: Record<string, unknown> = {};
  for (const fieldKey of fieldKeys) {
    const fieldGoal = (goal as Record<string, unknown>)[fieldKey];
    if (fieldGoal && typeof fieldGoal === 'object') {
      next[fieldKey] = fieldGoal;
    }
  }
  return Object.keys(next).length > 0 ? next : undefined;
}

type ActiveGoal = NonNullable<NonNullable<DashboardMetric['goals']>['active']>[number];

function filterActiveGoalsForFields(
  metric: DashboardMetric,
  fieldKeys: readonly string[]
): ActiveGoal[] | undefined {
  const activeGoals = metric.goals?.active ?? [];
  if (!Array.isArray(activeGoals) || activeGoals.length === 0) return undefined;

  const fieldSet = new Set(fieldKeys);
  const filtered: ActiveGoal[] = [];
  for (const goal of activeGoals) {
    const targets: NonNullable<ActiveGoal['targets']> = {};
    for (const [fieldKey, target] of Object.entries(goal.targets ?? {})) {
      if (fieldSet.has(fieldKey) && target && typeof target === 'object') {
        targets[fieldKey] = target;
      }
    }
    if (Object.keys(targets).length === 0) continue;
    filtered.push({ ...goal, targets });
  }

  return filtered.length > 0 ? filtered : undefined;
}

function splitMetricByFields(
  metric: DashboardMetric,
  fieldKeys: readonly string[],
  metricSuffix: string
): DashboardMetric {
  const metricPrefix = metric.metricId.includes('.')
    ? metric.metricId.split('.')[0]
    : 'gace';

  const series = (metric.series ?? []).map((point) => {
    const next: Record<string, unknown> = {
      quarter: point.quarter,
    };
    for (const fieldKey of fieldKeys) {
      if (isNumericFieldValue(point[fieldKey])) {
        next[fieldKey] = point[fieldKey];
      }
    }
    const fieldGoal = pickFieldsGoal(point.goal, fieldKeys);
    if (fieldGoal) {
      next.goal = fieldGoal;
    }
    if (point.goalStatus !== undefined) {
      next.goalStatus = point.goalStatus;
    }
    return next;
  });

  let allTime: Record<string, number> | null = null;
  if (metric.allTime) {
    const nextAllTime: Record<string, number> = {};
    for (const fieldKey of fieldKeys) {
      if (isNumericFieldValue(metric.allTime[fieldKey])) {
        nextAllTime[fieldKey] = metric.allTime[fieldKey];
      }
    }
    if (Object.keys(nextAllTime).length > 0) {
      allTime = nextAllTime;
    }
  }

  const fieldGoals = filterActiveGoalsForFields(metric, fieldKeys);

  return {
    ...metric,
    metricId: `${metricPrefix}.${metricSuffix}`,
    series,
    allTime,
    ...(fieldGoals ? { goals: { active: fieldGoals } } : { goals: { active: [] } }),
  };
}

function buildChartFieldGroups(availableFields: string[]): GaceChartGroup[] {
  const remaining = new Set(availableFields);
  const groups: GaceChartGroup[] = [];

  for (const group of GACE_CHART_GROUPS) {
    const presentFields = group.fields.filter((field) => remaining.has(field));
    if (presentFields.length === 0) continue;
    groups.push({ metricSuffix: group.metricSuffix, fields: presentFields });
    for (const field of presentFields) {
      remaining.delete(field);
    }
  }

  for (const field of sortFieldKeys(Array.from(remaining))) {
    groups.push({ metricSuffix: field, fields: [field] });
  }

  // Keep chart order aligned with GACE_FIELD_ORDER (group position = earliest member).
  return groups.sort((a, b) => {
    const aKey = a.fields[0] ?? a.metricSuffix;
    const bKey = b.fields[0] ?? b.metricSuffix;
    const order = new Map<string, number>(GACE_FIELD_ORDER.map((key, index) => [key, index]));
    // Place the awareness/literacy group near those fields' natural order.
    const aOrder =
      a.fields.some((f) => GACE_AWARENESS_LITERACY_FIELD_SET.has(f))
        ? Math.min(
            ...a.fields.map((f) => order.get(f) ?? Number.MAX_SAFE_INTEGER)
          )
        : order.get(aKey) ?? Number.MAX_SAFE_INTEGER;
    const bOrder =
      b.fields.some((f) => GACE_AWARENESS_LITERACY_FIELD_SET.has(f))
        ? Math.min(
            ...b.fields.map((f) => order.get(f) ?? Number.MAX_SAFE_INTEGER)
          )
        : order.get(bKey) ?? Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return a.metricSuffix.localeCompare(b.metricSuffix);
  });
}

export function isGaceProgramKpisMetric(metricId: string): boolean {
  const id = metricId.toLowerCase();
  return id === 'gace.programkpis' || id === 'programkpis';
}

/**
 * Expand combined gace.programKpis into chart metrics.
 * Most fields become their own chart; awareness / exposure / literacy share one chart.
 */
export function expandGaceMetricsForCharts(
  data: ComponentProjectMetricsResponse | undefined
): GaceChartMetric[] {
  if (!data?.sections?.length) return [];

  const expanded: GaceChartMetric[] = [];

  for (const section of data.sections) {
    for (const metric of section.metrics) {
      if (!isGaceProgramKpisMetric(metric.metricId)) {
        expanded.push({
          key: `${section.sectionId}-${metric.metricId}`,
          metric,
          sectionTitle: section.title,
        });
        continue;
      }

      const fieldKeys = sortFieldKeys(collectFieldKeys(metric));
      if (fieldKeys.length <= 1) {
        expanded.push({
          key: `${section.sectionId}-${metric.metricId}`,
          metric,
          sectionTitle: section.title,
        });
        continue;
      }

      for (const group of buildChartFieldGroups(fieldKeys)) {
        const split = splitMetricByFields(metric, group.fields, group.metricSuffix);
        expanded.push({
          key: `${section.sectionId}-${split.metricId}`,
          metric: split,
          sectionTitle: section.title,
        });
      }
    }
  }

  return expanded;
}

export function hasGaceComponentProjectMetricsContent(
  data: ComponentProjectMetricsResponse | undefined
): boolean {
  return expandGaceMetricsForCharts(data).some(
    (entry) => (entry.metric.series?.length ?? 0) > 0 || Boolean(entry.metric.allTime)
  );
}
